import is from '@sindresorhus/is';
import { useMemo } from 'react';

import type { JobProfileTaskTransport } from '~/shared/data-contract/profile-task';

import { useJobItemDetails } from '../../api/use-job-item-details';
import type { ProfileTask, ProfileTaskParentContext } from '../model/profile-task';
import { mapProfileTaskAutomation, type ProfileTaskAutomation } from './profile-task-automation-adapter';
import { mapJobProfileTaskDetail, ProfileTaskNotFoundError } from './profile-task-transport-adapter';

export type ProfileTaskQueryInput = Omit<ProfileTaskParentContext, 'parentVersion'> & {
    /** Language to resolve the task body into; the transport's resolved fields stand without it. */
    readonly language?: string;
    readonly parentVersion?: number;
    readonly taskId: string;
    readonly taskVersionId?: string;
};

export type UseProfileTaskResult = {
    readonly data: ProfileTask | undefined;
    readonly error: Error | null;
    /** The backend's automation assessment and its state; undefined until the task is loaded. */
    readonly automation: ProfileTaskAutomation | undefined;
    readonly isError: boolean;
    readonly isLoading: boolean;
    readonly refetch: () => void;
};

const findTask = (
    tasks: readonly JobProfileTaskTransport[] | undefined,
    taskId: string,
): JobProfileTaskTransport | undefined => tasks?.find((task) => task.taskId === taskId);

/**
 * Task detail is not a standalone read: the backend resolves the full task content into the
 * parent's profile payload, so the tile reads through the shared job item details query.
 *
 * A freshly generated task lives on a newer item version than the one the tile is pinned to
 * (creation repins the item server-side), so a miss falls back to the item's latest version.
 */
export const useProfileTask = ({
    architectureId,
    enabled = true,
    language,
    parentId,
    parentType,
    parentVersion,
    taskId,
}: ProfileTaskQueryInput & { readonly enabled?: boolean }): UseProfileTaskResult => {
    const itemType = parentType === 'role' ? ('job-role' as const) : ('job-level' as const);
    const pinnedVersion = is.number(parentVersion) ? String(parentVersion) : undefined;
    const details = useJobItemDetails({
        architectureId,
        enabled,
        itemId: parentId,
        itemType,
        version: pinnedVersion,
    });
    const pinnedTask = findTask(details.data?.jobProfile?.tasks, taskId);
    const isMissingInPinnedVersion = is.number(parentVersion) && details.data !== undefined && pinnedTask === undefined;

    const latestDetails = useJobItemDetails({
        architectureId,
        enabled: enabled && isMissingInPinnedVersion,
        itemId: parentId,
        itemType,
        version: undefined,
    });
    const latestTask = findTask(latestDetails.data?.jobProfile?.tasks, taskId);

    const transport = pinnedTask ?? latestTask;
    const data = useMemo(
        () => (transport === undefined ? undefined : mapJobProfileTaskDetail(transport, language)),
        [language, transport],
    );
    const automation = useMemo(
        () => (transport === undefined ? undefined : mapProfileTaskAutomation(transport)),
        [transport],
    );
    const isLoading =
        details.isLoading || (isMissingInPinnedVersion && latestDetails.isLoading && latestDetails.data === undefined);
    const isNotFound = !isLoading && details.data !== undefined && transport === undefined;

    return {
        data,
        automation,
        error: details.error ?? (isNotFound ? new ProfileTaskNotFoundError(taskId) : null),
        isError: details.isError || isNotFound,
        isLoading,
        refetch: () => {
            void details.refetch();
            if (isMissingInPinnedVersion) {
                void latestDetails.refetch();
            }
        },
    };
};
